export const NOTE_BUCKETS = Object.freeze({
  PRODUCTION: "production",
  PROJECT: "project",
  CUSTOMER: "customer",
  SALES: "sales",
  FINANCE: "finance",
  SYSTEM: "system",
});

export const NOTE_SCOPES = Object.freeze({
  PURCHASING: "purchasing",
  SHIPPING: "shipping",
  PROOFS: "proofs",
  PRODUCTION: "production",
  SUPPLIER: "supplier",
  RECEIVING: "receiving",
});

export const PRODUCTION_LEGACY_ACTIONS = Object.freeze([
  "production-next-action-date",
  "production-note",
  "purchasing-note",
  "purchase-order-note",
  "po-note",
  "po-status-change",
  "po-lifecycle-status-change",
  "po-next-action",
  "po-next-action-date",
  "supplier-email",
  "supplier-email-sent",
  "supplier-reply",
  "supplier-follow-up",
  "proof-note",
  "proof-request",
  "proof-requested",
  "proof-uploaded",
  "proof-approved",
  "proof-rejected",
  "production-rep-assigned",
  "production-rep-status",
  "shipping-note",
  "shipping-status-change",
  "tracking-note",
  "tracking-updated",
  "received-note",
  "partially-received-note",
]);

const PRODUCTION_SCOPES = new Set([
  NOTE_SCOPES.PRODUCTION,
  NOTE_SCOPES.PURCHASING,
  NOTE_SCOPES.SHIPPING,
  NOTE_SCOPES.PROOFS,
  NOTE_SCOPES.SUPPLIER,
  NOTE_SCOPES.RECEIVING,
]);

const PRODUCTION_SOURCES = new Set([
  "purchase-order",
  "po",
  "production",
  "purchasing",
  "shipping",
  "supplier",
]);

const PRODUCTION_EXCLUDED_ACTIONS = new Set([
  "written-project-note",
  "order-note",
  "lead-create",
  "lead-update",
  "ai-lead-assignment",
  "assignment",
  "chat-digest",
  "invoice-created",
  "invoice-sent",
  "payment-received",
  "inbound-project-email",
]);

const FINANCE_ACTIONS = new Set([
  "invoice-created",
  "invoice-sent",
  "payment-received",
]);

const SALES_ACTIONS = new Set([
  "lead-create",
  "lead-update",
  "ai-lead-assignment",
  "assignment",
]);

const PROJECT_ACTIONS = new Set([
  "written-project-note",
  "order-note",
]);

const SYSTEM_ACTIONS = new Set([
  "chat-digest",
]);

const normalize = (value) => String(value || "").trim().toLowerCase();

export const isProductionBucketNote = (note = {}) => {
  const explicitBucket = normalize(note?.noteBucket);
  if (explicitBucket) return explicitBucket === NOTE_BUCKETS.PRODUCTION;

  const action = normalize(note?.action);
  if (PRODUCTION_EXCLUDED_ACTIONS.has(action)) return false;

  const scope = normalize(note?.scope);
  if (PRODUCTION_SCOPES.has(scope)) return true;

  const source = normalize(note?.source);
  if (PRODUCTION_SOURCES.has(source)) return true;

  if (note?.poId != null || note?.purchaseOrderId != null) return true;

  return PRODUCTION_LEGACY_ACTIONS.includes(action);
};

export const getNoteBucket = (note = {}) => {
  const explicitBucket = normalize(note?.noteBucket);
  if (explicitBucket) return explicitBucket;
  if (isProductionBucketNote(note)) return NOTE_BUCKETS.PRODUCTION;

  const action = normalize(note?.action);
  if (FINANCE_ACTIONS.has(action)) return NOTE_BUCKETS.FINANCE;
  if (SALES_ACTIONS.has(action)) return NOTE_BUCKETS.SALES;
  if (PROJECT_ACTIONS.has(action)) return NOTE_BUCKETS.PROJECT;
  if (SYSTEM_ACTIONS.has(action)) return NOTE_BUCKETS.SYSTEM;
  return "";
};

export const isProjectBucketNote = (note = {}) =>
  getNoteBucket(note) === NOTE_BUCKETS.PROJECT;

export const isFinanceBucketNote = (note = {}) =>
  getNoteBucket(note) === NOTE_BUCKETS.FINANCE;

export const isSalesBucketNote = (note = {}) =>
  getNoteBucket(note) === NOTE_BUCKETS.SALES;
